import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatMinutesAsTime, getYearGroup } from '@/utils/analyticsHelpers';
import { fromZonedTime } from 'date-fns-tz';

interface PauseTimeByYearGroupProps {
  shopId: string;
  startDate: string;
  endDate: string;
  shopTimezone: string;
}

interface YearGroupPauseStats {
  yearGroup: string;
  totalPauseMinutes: number;
  avgPausePerTicket: number;
  ticketCount: number;
}

interface TechPauseStats {
  techId: string;
  techName: string;
  totalPauseMinutes: number;
  count: number;
}

export function PauseTimeByYearGroup({ shopId, startDate, endDate, shopTimezone }: PauseTimeByYearGroupProps) {
  const [loading, setLoading] = useState(true);
  const [yearGroupStats, setYearGroupStats] = useState<YearGroupPauseStats[]>([]);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [techDrilldown, setTechDrilldown] = useState<TechPauseStats[]>([]);
  const [loadingDrilldown, setLoadingDrilldown] = useState(false);

  useEffect(() => {
    loadYearGroupPauseStats();
    // KPI-R70-FIX (MED-1): shopTimezone dep — fromZonedTime inside would
    // otherwise use a stale TZ for UTC bounds after a shop switch.
  }, [shopId, startDate, endDate, shopTimezone]);

  async function loadYearGroupPauseStats() {
    setLoading(true);
    try {
      const startUtc = fromZonedTime(`${startDate}T00:00:00`, shopTimezone).toISOString();
      const endUtc = fromZonedTime(`${endDate}T23:59:59`, shopTimezone).toISOString();

      // Get pause sessions - only completed ones (with duration)
      // This naturally excludes active pauses on parking lot tickets
      const { data: pauseSessions } = await supabase
        .from('ticket_pause_sessions')
        .select('id, ticket_id, duration_minutes')
        .eq('shop_id', shopId)
        .gte('paused_at', startUtc)
        .lte('paused_at', endUtc)
        .not('duration_minutes', 'is', null);

      if (!pauseSessions?.length) {
        setYearGroupStats([]);
        setLoading(false);
        return;
      }

      // Get ticket vehicle info
      const ticketIds = [...new Set(pauseSessions.map(s => s.ticket_id))];
      const { data: tickets } = await supabase
        .from('tickets')
        .select('id, vehicle_id')
        .in('id', ticketIds);

      const vehicleIds = [...new Set(tickets?.map(t => t.vehicle_id).filter(Boolean) || [])];
      const { data: vehicles } = await supabase
        .from('vehicles')
        .select('id, year')
        .in('id', vehicleIds);

      const vehicleYearMap = new Map(vehicles?.map(v => [v.id, v.year]) || []);
      const ticketYearGroupMap = new Map<string, string>();
      tickets?.forEach(t => {
        const year = vehicleYearMap.get(t.vehicle_id);
        if (year) ticketYearGroupMap.set(t.id, getYearGroup(year));
      });

      // Aggregate by year group
      const groupMap = new Map<string, { pause: number; tickets: Set<string> }>();
      
      pauseSessions.forEach(s => {
        const yearGroup = ticketYearGroupMap.get(s.ticket_id) || 'Unknown';
        const existing = groupMap.get(yearGroup) || { pause: 0, tickets: new Set() };
        existing.pause += s.duration_minutes || 0;
        existing.tickets.add(s.ticket_id);
        groupMap.set(yearGroup, existing);
      });

      const stats: YearGroupPauseStats[] = Array.from(groupMap.entries())
        .filter(([group]) => group !== 'Unknown')
        .map(([yearGroup, data]) => ({
          yearGroup,
          totalPauseMinutes: data.pause,
          avgPausePerTicket: data.tickets.size > 0 ? Math.round(data.pause / data.tickets.size) : 0,
          ticketCount: data.tickets.size,
        }))
        .sort((a, b) => {
          const aYear = parseInt(a.yearGroup.split('–')[0]);
          const bYear = parseInt(b.yearGroup.split('–')[0]);
          return bYear - aYear;
        });

      setYearGroupStats(stats);
    } catch (error) {
      console.error('Error loading year group pause stats:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadTechDrilldown(yearGroup: string) {
    setLoadingDrilldown(true);
    try {
      const startUtc = fromZonedTime(`${startDate}T00:00:00`, shopTimezone).toISOString();
      const endUtc = fromZonedTime(`${endDate}T23:59:59`, shopTimezone).toISOString();
      const [startYear, endYear] = yearGroup.split('–').map(Number);

      // Get vehicles in this year range
      const { data: vehicles } = await supabase
        .from('vehicles')
        .select('id')
        .gte('year', startYear)
        .lte('year', endYear);

      if (!vehicles?.length) {
        setTechDrilldown([]);
        return;
      }

      const vehicleIds = vehicles.map(v => v.id);

      // Get tickets for these vehicles
      const { data: tickets } = await supabase
        .from('tickets')
        .select('id')
        .eq('shop_id', shopId)
        .in('vehicle_id', vehicleIds);

      const ticketIds = tickets?.map(t => t.id) || [];

      // Get pause sessions with technician info
      const { data: pauseSessions } = await supabase
        .from('ticket_pause_sessions')
        .select('technician_id, duration_minutes')
        .eq('shop_id', shopId)
        .in('ticket_id', ticketIds)
        .gte('paused_at', startUtc)
        .lte('paused_at', endUtc)
        .not('duration_minutes', 'is', null)
        .not('technician_id', 'is', null);

      // Get technician names
      const techIds = [...new Set(pauseSessions?.map(s => s.technician_id).filter(Boolean) || [])];
      const { data: technicians } = await supabase
        .from('technicians')
        .select('id, name')
        .in('id', techIds);

      const techNameMap = new Map(technicians?.map(t => [t.id, t.name]) || []);

      // Aggregate by technician
      const techMap = new Map<string, { pause: number; count: number; name: string }>();
      
      pauseSessions?.forEach(s => {
        if (!s.technician_id) return;
        const techName = techNameMap.get(s.technician_id) || 'Unknown';
        const existing = techMap.get(s.technician_id) || { pause: 0, count: 0, name: techName };
        existing.pause += s.duration_minutes || 0;
        existing.count += 1;
        techMap.set(s.technician_id, existing);
      });

      const stats: TechPauseStats[] = Array.from(techMap.entries())
        .map(([techId, data]) => ({
          techId,
          techName: data.name,
          totalPauseMinutes: data.pause,
          count: data.count,
        }))
        .sort((a, b) => b.totalPauseMinutes - a.totalPauseMinutes);

      setTechDrilldown(stats);
    } catch (error) {
      console.error('Error loading tech drilldown:', error);
    } finally {
      setLoadingDrilldown(false);
    }
  }

  function handleGroupClick(yearGroup: string) {
    if (expandedGroup === yearGroup) {
      setExpandedGroup(null);
      setTechDrilldown([]);
    } else {
      setExpandedGroup(yearGroup);
      loadTechDrilldown(yearGroup);
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pause Time by Year Group</CardTitle>
        <CardDescription>
          Click a year group to see technician breakdown
        </CardDescription>
      </CardHeader>
      <CardContent>
        {yearGroupStats.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No pause data in this date range</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Year Group</TableHead>
                <TableHead className="text-right">Total Pause</TableHead>
                <TableHead className="text-right">Avg/Ticket</TableHead>
                <TableHead className="text-right">Tickets</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {yearGroupStats.map((group) => (
                <>
                  <TableRow 
                    key={group.yearGroup}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleGroupClick(group.yearGroup)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {expandedGroup === group.yearGroup ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        {group.yearGroup}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{formatMinutesAsTime(group.totalPauseMinutes)}</TableCell>
                    <TableCell className="text-right">{formatMinutesAsTime(group.avgPausePerTicket)}</TableCell>
                    <TableCell className="text-right">{group.ticketCount}</TableCell>
                  </TableRow>
                  {expandedGroup === group.yearGroup && (
                    <TableRow>
                      <TableCell colSpan={4} className="bg-muted/30 p-4">
                        {loadingDrilldown ? (
                          <div className="flex justify-center py-4">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </div>
                        ) : techDrilldown.length === 0 ? (
                          <p className="text-muted-foreground text-sm text-center">No technician data</p>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Technician</TableHead>
                                <TableHead className="text-right">Total Pause</TableHead>
                                <TableHead className="text-right">Pauses</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {techDrilldown.map((tech) => (
                                <TableRow key={tech.techId}>
                                  <TableCell>{tech.techName}</TableCell>
                                  <TableCell className="text-right">{formatMinutesAsTime(tech.totalPauseMinutes)}</TableCell>
                                  <TableCell className="text-right">{tech.count}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
